import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import PaginationControls from '../../utils/PaginationControls';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
// Edit hint: not clipped by the scrolling list, flips below when the top
// row has no room above.
import '../../utils/smartTooltip';

const FormThreadsTree = ({ 
  onSelectThread, 
  selectedThreadId, 
  selectedThreadIds, 
  fabricId,
  multiSelect = false,
  selection = true,
  apiBaseUrl,
  onViewThread,       // (threadId) => void
  onEditThread,       // (thread) => void
  showThreadActions = false,
  showFolderActions = false,  // pencil in selection mode — project entry only
  onEditFolder,               // (threadId) => void
  // true when the viewer has no edit/delete rights on the project itself
  // ("View only" up top). A folder can still show Edit here because its
  // access is decided per-folder, across every project it's linked to — not
  // just this one. Only meaningful to explain in that mismatched state.
  projectViewOnly = false,
}) => {
  // Threads data
  const [threadsTree, setThreadsTree] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [total, setTotal] = useState(0);
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');

  const navigate = useNavigate();

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Fetch threads on component mount and when pagination or search changes
  useEffect(() => {
    fetchThreadsTree();
  }, [page, size, search, fabricId, selection]);

  // Fetch threads tree from API
  const fetchThreadsTree = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading...');
    
    try {
      // Create request body with pagination and search parameters
      const requestBody = {
        page,
        size,
        search: search || undefined
      };
      
      // Only filter by fabricId when selection is false (view-only mode)
      // When selection is true, we want to show all threads
      if (!selection && fabricId) {
        requestBody.fabric_id = fabricId;
      }
      const fullUrl = `${apiBaseUrl}/threads-tree`;
      const response = await axios.post(
        fullUrl,
        requestBody,
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load Data folders');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setThreadsTree(data.data || []);
      setTotal(data.total || 0);
      
    } catch (error) {
      console.error('Error fetching Data folders tree:', error);
      setPopupErrorMessage(error.message || 'Failed to load Data folders');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();   // stop form submission
      handleSearch();
    }
  };
  
  // Toggle thread expansion
  const toggleThreadExpansion = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };
  
  // Handle thread selection
  const handleSelectThread = (threadId) => {
    if (onSelectThread && selection) {
      onSelectThread(threadId);
    }
  };

  // Handle click on thread row (for selection=false mode)
  const handleThreadRowClick = (threadId) => {
    if (!selection && onSelectThread) {
      onSelectThread(threadId);
    }
  };

  // Check if a thread is selected (supports both single and multi-select modes)
  const isThreadSelected = (threadId) => {
    if (multiSelect && selectedThreadIds) {
      return selectedThreadIds.includes(threadId);
    } else {
      return selectedThreadId === threadId;
    }
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  return (
    <div className="ev-card-3">
      {/* Search input with button */}

      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* Left side - Search */}
        <div className="col-5">
          <div className="position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search Data folders"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
            <button 
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button" 
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>
            </button>        
          </div>
        </div>

        {/* Right side - Pagination and Size Selection */}
        <div className="col-7 d-flex justify-content-end align-items-center">
          
          <div className="d-flex align-items-center">
            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>

            <span className="ev-xsmall-p-dark mx-2">
              Page {page}/{Math.ceil(total / size) || 1}
            </span>

            <button 
              type="button"
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>

          <div className="d-flex align-items-center ms-2">
            <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
      
      <div className="scrollable-element vh-25">
        {threadsTree.length === 0 ? (
          <p className="ev-small-p-blue text-center mt-2">No Data folders found</p>
        ) : (
          <div className="thread-tree">
            {threadsTree.map(thread => (
              <div key={thread.id} className="margin-bottom-1">
                <div 
                  className={` d-flex align-items-center ${!selection ? 'thread-row' : ''}`} 
                  onClick={!selection ? () => handleThreadRowClick(thread.id) : undefined}
                >
                  <button
                    type="button"
                    className="btn btn-sm ev-btn-text-only-fill pb-2 "
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click in selection=false mode
                      toggleThreadExpansion(thread.id);
                    }}
                  >
                    <i className={` bi ${expandedThreads[thread.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                  </button>
                  
                  {selection ? (
                    <div className="form-check mb-0 d-flex align-items-center ps-0 flex-grow-1">
                      <input
                        className="form-check-input mt-0 ms-0 me-2 flex-shrink-0"
                        type={multiSelect ? "checkbox" : "radio"}
                        id={`thread-${thread.id}`}
                        name={multiSelect ? `thread-${thread.id}` : "selectedThread"}
                        checked={isThreadSelected(thread.id)}
                        onChange={() => handleSelectThread(thread.id)}
                      />
                      <label className="form-check-label" htmlFor={`thread-${thread.id}`}>
                        <span className="ev-small-p-blue">{thread.name}</span> &nbsp;
                        {/* <span className={`badge  ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                          {thread.thread_access_level}
                        </span> */}
                        <span className="ev-message-card1 ms-1 ev-btn-xsmall ev-fs-11" >
                          {thread.document_count} docs
                        </span>
                      </label>

                      {/* Project entry only — edit this Data Folder in Data Manager.
                          can_edit answers per row, the same way the Edit button in
                          the non-selection branch below is decided. */}
                      {showFolderActions && thread.can_edit && (
                        <button
                          type="button"
                          className={`ev-btn-lightblue-fill-round ev-radius-xsmall ev-btn-small ms-auto me-2 flex-shrink-0${projectViewOnly ? ' smart-tooltip' : ''}`}
                          data-tooltip={projectViewOnly ? 'Editable through another project this folder is shared with' : undefined}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (onEditFolder) onEditFolder(thread.id);
                          }}
                        >
                          <i className="bi bi-pencil ev-fs-11"></i>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <span className={`ev-small-p-blue ${isThreadSelected(thread.id) ? 'fw-bold' : ''}`}>{thread.name}</span>
                      {/* <span className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                        {thread.thread_access_level}
                      </span> */}
                      <span className="ev-message-card1 ev-btn-xsmall ev-fs-11">
                        {thread.document_count} docs
                      </span>
                      {showThreadActions && (
                        <>
                          <button
                            type="button"
                            className="ev-btn-lightblue-fill-round ev-btn-small"
                            onClick={(e) => { e.stopPropagation(); onViewThread && onViewThread(thread.id); }}
                          >
                            <i className="bi bi-eye"></i> view
                          </button>
                          {thread.can_edit && (
                            <button
                              type="button"
                              className={`ev-btn-lightblue-fill-round ev-btn-small${projectViewOnly ? ' smart-tooltip' : ''}`}
                              data-tooltip={projectViewOnly ? 'Editable through another project this folder is shared with' : undefined}
                              onClick={(e) => { e.stopPropagation(); onEditThread && onEditThread(thread); }}
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {expandedThreads[thread.id] && thread.documents && thread.documents.length > 0 && (
                  <div className="ms-5 mt-0">
                    {thread.documents.map(doc => (
                      <div key={doc.id} className="ev-xsmall-p-dark mb-1">
                        <i className="bi bi-file-earmark-text"></i> {doc.document_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </div>
  );
};

export default FormThreadsTree;